import { observable } from 'mobx';
import { IFilterQuery } from '../../views/product-filters/IProductFilters';
import { toCamelCase } from './../../utils/ToCamelCase';
import { Sku } from './Sku';

export interface IFilterMapItem {
    keyType: string;
    keyName: string;
}

export enum filterQueryLevel {
    SKU = 'sku',
    PRODUCT = 'product'
}

export enum filterQueryKeyTypes {
    MACHINE_NAME = 'machine_name',
    ARRAY_OF_OBJECTS = 'array_of_objects',
    OBJECT = 'object'
}

export class FilterQuery {
    @observable private map: IFilterMapItem[];
    @observable public level: filterQueryLevel;

    constructor(filterQueryJson: IFilterQuery) {
        this.level = filterQueryJson.level;

        if (!filterQueryJson.map.map) {
            this.map = [];

            return;
        }

        this.map = filterQueryJson.map.map((mapItem) => {
            return {
                keyType: mapItem.key_type,
                keyName: toCamelCase(mapItem.key_name)
            };
        });
    }

    public extract<Type>(entity: Type, useSimulated: boolean = false) {
        return this.extractFromEntity(entity, this.map, this.level, useSimulated);
    }

    private extractFromEntity(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        entityData: any,
        filterMap: IFilterMapItem[],
        level: string = filterQueryLevel.PRODUCT,
        useSimulated: boolean = false
    ): string[] {
        if (level === filterQueryLevel.SKU) {
            if (entityData instanceof Sku) {
                return [...new Set(this.extractFromEntity(entityData, filterMap))];
            }
            const skus = useSimulated ? entityData.simulatedSkus : entityData.initialSkus;
            const filterQueryValues: string[] = entityData.skus
                ? skus.reduce((extractedValues: string[], sku: Sku) => {
                      const currentSkuValues: string[] = this.extractFromEntity(sku, filterMap);

                      return extractedValues.concat(currentSkuValues);
                  }, [])
                : this.extractFromEntity(entityData.parentSku, filterMap);

            return [...new Set(filterQueryValues)];
        }

        const _filterMap = [...filterMap];
        const currentItem = _filterMap.length ? _filterMap.shift() : null;

        if (currentItem && entityData) {
            const _entityData = entityData[currentItem.keyName];

            if (currentItem.keyType === filterQueryKeyTypes.MACHINE_NAME) {
                return _entityData;
            } else if (currentItem.keyType === filterQueryKeyTypes.ARRAY_OF_OBJECTS) {
                if (!_entityData || !_entityData.length) {
                    return [''];
                }

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                return _entityData.reduce((allItemsValues: string[], item: any) => {
                    return allItemsValues.concat(this.extractFromEntity(item, _filterMap));
                }, []);
            }

            return this.extractFromEntity(_entityData, _filterMap);
        }

        return [''];
    }
}
